import React, { useState } from 'react';
import Button from 'components/UI/Button';
import { connect } from 'react-redux';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';
import Row from 'components/UI/Layout/Row';
import { buildCurrentMemberSelector } from 'store/selectors/members.selector';
import { saveProfileMember } from 'store/dispatchers/members.dispatchers';
import PropTypes from 'prop-types';
import EditableTextInput from 'components/UI/Input/EditableTextInput';
import FormComponent from 'components/UI/Input/FormComponent';
import { common as memberRules } from 'services/validation/rules/member.rules';
import produce from 'immer';
import EditableSelect from 'components/UI/Input/EditableSelect';
import ErrorMessage from 'components/UI/ErrorMessage';
import { buildForm } from './buildForm';

function UserInfoForm({
    authMember, onCancel, onSuccess, saveProfile,
})
{
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [memberForm, setMemberForm] = useState(
        {
            ...buildForm(memberRules),
            data: {
                id: authMember.id,
                prefix: authMember.prefix,
                job_title: authMember.job_title,
                best_contact_time: { value: authMember.best_contact_time, label: authMember.best_contact_time },
            },
        },
    );

    const bestContactTime = [
        'Week Days',
        'Week Days / Mornings (06:00 - 12:00)',
        'Week Days / Afternoons (12:00 - 18:00)',
        'Week Days / Evenings (18:00 - 24:00)',
        'Weekends',
        'Weekends / Mornings (06:00 - 12:00)',
        'Weekends / Afternoons (12:00 - 18:00)',
        'Weekends / Evenings (18:00 - 24:00)',
    ].map((prefix) => ({ label: prefix, value: prefix }));

    const updateDataForm = (name, value) =>
    {
        const form = produce(memberForm, (draft) =>
        {
            draft.data[name] = value;
        });

        setMemberForm({ ...form });
    };

    const cancelForm = async () =>
    {
        if (onCancel)
        {
            onCancel();
        }
    };

    const handleForm = async () =>
    {
        setIsLoading(true);
        setErrorMessage('');
        saveProfile({ ...memberForm.data, best_contact_time: memberForm.data.best_contact_time.value }).then((r) =>
        {
            if (r.response_code !== 200)
            {
                if (r.messages && r.messages.length > 0)
                {
                    throw new Error(r.messages[0]);
                }
                throw new Error('Error sending information, try again!');
            }

            if (onSuccess)
            {
                onSuccess({ ...authMember, ...memberForm.data, best_contact_time: memberForm.data.best_contact_time.value });
            }
        }).catch((e) =>
        {
            setErrorMessage(e.message ?? e);
        }).finally(() =>
        {
            setIsLoading(false);
        });
    };

    return (
        <FormComponent
            className="tw-gap-2"
            source={memberForm}
            onFormChange={
                () =>
                {}
            }
        >
            <ErrorMessage errorMessage={errorMessage} />
            <LoadingSpinner loading={isLoading} className="tw-mr-1" />
            <Row className="tw-py-2">
                <h2 className="tw-text-xl tw-font-bold dark:tw-text-neutral-300">Edit Profile</h2>
            </Row>
            <Row className="tw-py-1 tw-gap-1">
                <EditableTextInput
                    containerClassName="tw-py-2 tw-w-1/2"
                    labelText="Prefix"
                    text={memberForm.data.prefix}
                    editing
                    inputProps={{
                        path: 'value',
                        disabled: isLoading,
                        error: memberForm.errorsBag.errors.prefix,
                        value: memberForm.data.prefix,
                        placeholder: 'Eg: Mr, Mrs, Sr ...',
                        onChange: (value) => updateDataForm('prefix', value),
                    }}
                />
                <EditableTextInput
                    containerClassName="tw-py-2 tw-w-1/2"
                    labelText="Job title"
                    text={memberForm.data.job_title}
                    editing
                    inputProps={{
                        path: 'job_title',
                        disabled: isLoading,
                        value: memberForm.data.job_title,
                        error: memberForm.errorsBag.errors.job_title,
                        onChange: (value) => updateDataForm('job_title', value),
                    }}
                />
            </Row>
            <Row className="tw-py-1 tw-gap-1">
                <EditableTextInput
                    containerClassName="tw-py-2 tw-w-1/2"
                    labelText="Address"
                    text={memberForm.data.address}
                    editing
                    inputProps={{
                        path: 'address',
                        disabled: isLoading,
                        value: memberForm.data.address,
                        error: memberForm.errorsBag.errors.address,
                        onChange: (value) => updateDataForm('address', value),
                    }}
                />
                <EditableSelect
                    editing
                    containerClassName="tw-flex-wrap tw-py-2 tw-w-1/2"
                    labelContainerClassName="tw-flex-auto tw-w-full"
                    inputProps={{
                        path: 'best_contact_time',
                        getOptionLabel: (option) => option?.label,
                        getOptionValue: (option) => option.value,
                        options: bestContactTime,
                        error: memberForm.errorsBag.errors.best_contact_time,
                        disabled: isLoading,
                        value: memberForm.data.best_contact_time,
                        onChange: (value) => updateDataForm('best_contact_time', value),
                    }}
                    labelText="Best Contact Time"
                />
            </Row>

            <div className="tw-ml-8 tw-text-right">
                <Button onClick={cancelForm} customBg="tw-bg-white tw-text-black">Cancel</Button>
                <Button onClick={handleForm} type="primary" margin="tw-ml-2">Save</Button>
            </div>

        </FormComponent>
    );
}

UserInfoForm.propTypes = {
    authMember: PropTypes.object,
    saveProfile: PropTypes.func,
    onCancel: PropTypes.func,
    onSuccess: PropTypes.func,
};
const mapStateToProps = () =>
{
    const authMemberSelector = buildCurrentMemberSelector();

    return (state) => ({
        authMember: authMemberSelector(state),
    });
};

const mapDispatchToProps = (dispatch) => ({
    saveProfile(data)
    {
        return dispatch(saveProfileMember(data));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(UserInfoForm);
